import { DocumentDto } from '@modeso/aumico-lib-cpmm-fe';
import { FilePreviewStatus } from '@modeso/aumico-lib-cpmm-fe/enums/FilePreviewStatus.enum';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { DocumentsActions } from '../actions/workflow-documents.actions';

export const DOCUMENTS_FEATURE_KEY = 'documents-key';
export interface DocumentsState extends EntityState<DocumentDto> {
    loading: boolean;
    error: any;
    exportBlob: Blob | null;
}

export const adapter: EntityAdapter<DocumentDto> = createEntityAdapter<DocumentDto>({
    selectId: (document) => document.fileId,
});

export const initialState: DocumentsState = adapter.getInitialState({
    loading: false,
    error: null,
    exportBlob: null,
});

// Feature
export const selectDocumentsState = createFeatureSelector<DocumentsState>(DOCUMENTS_FEATURE_KEY);

const { selectAll, selectEntities, selectIds, selectTotal } = adapter.getSelectors();

export const selectAllDocuments = createSelector(selectDocumentsState, selectAll);

export const selectDocumentsEntities = createSelector(selectDocumentsState, selectEntities);

export const selectExportBlob = createSelector(selectDocumentsState, (state) => state.exportBlob);

export const selectDocumentById = (id: string) => createSelector(selectDocumentsEntities, (entities) => entities[id]);

const documentsReducerInternal = createReducer(
    initialState,

    // Load Task Documents
    on(DocumentsActions.loadTaskDocuments, (state) => ({ ...state, loading: true })),
    on(DocumentsActions.loadTaskDocumentsSuccess, (state, { documents }) => {
        return adapter.setAll(documents, { ...state, loading: false });
    }),
    on(DocumentsActions.loadTaskDocumentsFailure, (state, { error }) => ({ ...state, loading: false, error })),

    // Upload Document
    on(DocumentsActions.uploadDocument, (state) => ({ ...state, loading: true })),
    on(DocumentsActions.uploadDocumentSuccess, (state, { documents }) =>
        adapter.addMany(documents, {
            ...state,
            loading: false,
        }),
    ),
    on(DocumentsActions.uploadDocumentFailure, (state, { error }) => ({ ...state, loading: false, error })),

    // Update Document Metadata
    on(DocumentsActions.updateDocumentMetadata, (state) => {
        return { ...state, loading: true };
    }),
    on(DocumentsActions.updateDocumentMetadataSuccess, (state, { document }) => {
        return adapter.updateOne({ id: document.fileId, changes: document }, { ...state, loading: false });
    }),
    on(DocumentsActions.updateDocumentMetadataFailure, (state, { error }) => {
        return { ...state, loading: false, error };
    }),

    // Export Documents
    on(DocumentsActions.exportProjectDocuments, (state) => ({ ...state, loading: true })),
    on(DocumentsActions.exportProjectDocumentsSuccess, (state, { blob }) => ({
        ...state,
        exportBlob: blob,
        loading: false,
    })),
    on(DocumentsActions.exportProjectDocumentsFailure, (state, { error }) => ({ ...state, loading: false, error })),

    // Delete Document
    on(DocumentsActions.deleteDocument, (state) => ({ ...state, loading: true })),
    on(DocumentsActions.deleteDocumentSuccess, (state, { documentId }) => {
        return adapter.removeOne(documentId, { ...state, loading: false });
    }),
    on(DocumentsActions.deleteDocumentFailure, (state, { error }) => ({ ...state, loading: false, error })),

    // Preview field updates
    on(DocumentsActions.checkPreviewStatusSuccess, (state, { fileId, status }) =>
        adapter.updateOne({ id: fileId, changes: { previewStatus: status } }, state),
    ),
    on(DocumentsActions.checkPreviewStatusFailure, (state, { fileId }) =>
        adapter.updateOne({ id: fileId, changes: { previewStatus: FilePreviewStatus.FAILED } }, state),
    ),
    on(DocumentsActions.requestPreviewGeneration, (state, { fileId }) =>
        adapter.updateOne({ id: fileId, changes: { previewStatus: FilePreviewStatus.PENDING } }, state),
    ),
    on(DocumentsActions.loadPreviewUrlSuccess, (state, { fileId, url }) =>
        adapter.updateOne(
            { id: fileId, changes: { previewUrl: url, previewStatus: FilePreviewStatus.GENERATED } },
            state,
        ),
    ),
    on(DocumentsActions.loadPreviewUrlFailure, (state, { fileId }) =>
        adapter.updateOne({ id: fileId, changes: { previewStatus: FilePreviewStatus.FAILED } }, state),
    ),
);

export function documentsReducer(state: DocumentsState | undefined, action: Action) {
    return documentsReducerInternal(state, action);
}
